import { useParams, useLocation } from "react-router-dom";
import { usePersonExists } from "@/hooks/usePersonExists";
import { usePersonNavLinks } from "../_utils";
import { useQuery } from "@apollo/client";
import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import { renderName } from "@/lib/personHelpers";
import usePageTitle from "@/hooks/usePageTitle";

export interface PersonErrorHandlingState {
  pgaId: string | null;
  personNotFound: boolean;
  loading: boolean;
  navLinks: ReturnType<typeof usePersonNavLinks>;
  shouldRenderError: boolean;
}

/**
 * Hook that centralizes person existence checking, error state logic, and page title management.
 * This prevents code duplication across all person profile layout files.
 *
 * Usage:
 * ```tsx
 * const { pgaId, personNotFound, navLinks, shouldRenderError } = usePersonErrorHandling();
 *
 * if (shouldRenderError) {
 *   return (
 *     <AppLayout navLinks={[]} interior bottomBar>
 *       <PersonNotFoundErrorBoundary />
 *     </AppLayout>
 *   );
 * }
 * ```
 */
export function usePersonErrorHandling(): PersonErrorHandlingState {
  const { pgaId } = useParams();
  const location = useLocation();
  const { personNotFound, loading } = usePersonExists(pgaId);
  const navLinks = usePersonNavLinks(pgaId || "");

  // Fetch person data for page title
  const { data } = useQuery(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId: pgaId || "" },
    skip: !pgaId
  });

  // Set page title with person name
  const personName = data?.person
    ? renderName(
        data.person.displayName,
        data.person.informalName,
        data.person.preferredLastName,
        data.person.suffixName,
        data.person.middleName,
        data.person.firstName
      )
    : "";
  usePageTitle(location.pathname, personName ? `People | ${personName}` : "People");

  // Handle missing pgaId
  if (!pgaId) {
    return {
      pgaId: null,
      personNotFound: false,
      loading: false,
      navLinks: {},
      shouldRenderError: true
    };
  }

  return {
    pgaId,
    personNotFound,
    loading,
    navLinks,
    shouldRenderError: personNotFound
  };
}
